import axios from 'axios';
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ParametresSecurite } from "@/components/securite/ParametresSecurite";
import { HistoriqueSecurite } from "@/components/securite/HistoriqueSecurite"; // 🔐 Connexion P5
import { SectionStockage } from "@/components/securite/SectionStockage"; // 📦 Phase 6
import { useSearchParams } from "react-router-dom";
import {
  Shield, Lock, Download, RefreshCw, CheckCircle, AlertTriangle,
  Database, Clock, Loader2, Eye, EyeOff, Key
} from "lucide-react";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export const Securite = () => {
  const [searchParams] = useSearchParams(); // 🔐 P5 : /securite?onglet=historique&utilisateur=…
  const [config, setConfig] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backingUp, setBackingUp] = useState(false);

  // PIN states
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [showPinChange, setShowPinChange] = useState(false);
  const [pinForm, setPinForm] = useState({ pin: "", confirmation: "" });
  const [changeForm, setChangeForm] = useState({ ancien: "", nouveau: "", confirmation: "" });
  const [showPins, setShowPins] = useState(false);
  const [savingPin, setSavingPin] = useState(false);

  // Login logs
  const [loginLogs, setLoginLogs] = useState(null);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cfgRes, bkpRes] = await Promise.all([
        axios.get(`${API_URL}/api/security/config`),
        axios.get(`${API_URL}/api/security/backup/list`)
      ]);
      const cfgData = cfgRes.data;
      const bkpData = bkpRes.data;
      setConfig(cfgData);
      setBackups(bkpData.backups || []);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };

  const setupPin = async () => {
    if (!pinForm.pin || pinForm.pin.length < 4) {
      toast.error("Le PIN doit faire au moins 4 chiffres"); return;
    }
    if (pinForm.pin !== pinForm.confirmation) {
      toast.error("Les PINs ne correspondent pas"); return;
    }
    setSavingPin(true);
    try {
      const res = await axios.post(`${API_URL}/api/security/pin/setup`, { pin: pinForm.pin, pin_confirmation: pinForm.confirmation });
      toast.success("✅ PIN configuré ! Toutes les suppressions demanderont ce PIN.");
      setShowPinSetup(false);
      setPinForm({ pin: "", confirmation: "" });
      loadAll();
    } catch (e) { 
      const errorMsg = e.response?.data?.detail || e.message || "Erreur lors de la configuration du PIN";
      toast.error(errorMsg); 
    }
    finally { setSavingPin(false); }
  };

  const changePin = async () => {
    if (changeForm.nouveau !== changeForm.confirmation) {
      toast.error("Les nouveaux PINs ne correspondent pas"); return;
    }
    if (changeForm.nouveau.length < 4) {
      toast.error("Le nouveau PIN doit faire au moins 4 chiffres"); return;
    }
    setSavingPin(true);
    try {
      const res = await axios.put(`${API_URL}/api/security/pin/change`, {
          ancien_pin: changeForm.ancien,
          nouveau_pin: changeForm.nouveau,
          confirmation: changeForm.confirmation
        });
      toast.success("✅ PIN modifié avec succès");
      setShowPinChange(false);
      setChangeForm({ ancien: "", nouveau: "", confirmation: "" });
    } catch (e) { 
      const errorMsg = e.response?.data?.detail || e.message || "Erreur lors de la modification du PIN";
      toast.error(errorMsg); 
    }
    finally { setSavingPin(false); }
  };

  const runBackup = async () => {
    setBackingUp(true);
    try {
      const res = await axios.post(`${API_URL}/api/security/backup/now`);
      const data = res.data;
      toast.success(`✅ Backup terminé ! ${data.details?.total_docs?.toLocaleString() || '0'} docs sauvegardés`);
      loadAll();
    } catch (e) { 
      const errorMsg = e.response?.data?.detail || e.message || "Erreur lors de la sauvegarde";
      toast.error("Erreur backup: " + errorMsg); 
    }
    finally { setBackingUp(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Sécurité &amp; Sauvegarde</h1>
        <p className="text-slate-500 text-sm mt-1">Protégez vos données et configurez les accès aux actions sensibles</p>
      </div>

      {/* 🔐 Connexion P5 — historique de sécurité (en tête si demandé par lien ?onglet=historique) */}
      {searchParams.get('onglet') === 'historique' && <div id="historique"><HistoriqueSecurite utilisateurInitial={searchParams.get('utilisateur') || null} typesInitiaux={(searchParams.get('types') || '').split(',').filter(Boolean)} /></div>}

      {/* 🔐 Connexion P2 — paramètres configurables (règles, verrouillage, session, actions) */}
      <ParametresSecurite />

      {searchParams.get('onglet') !== 'historique' && <HistoriqueSecurite />}

      {/* 📦 Phase 6 — stockage des fichiers (santé + rapatriement, propriétaire) */}
      <SectionStockage />

      {/* SECTION PIN */}
      <Card className={config?.pin_configured ? 'border-green-200' : 'border-orange-200 bg-orange-50'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5 text-blue-600" />
            Code PIN de suppression
            {config?.pin_configured
              ? <Badge className="bg-green-100 text-green-700 ml-2"><CheckCircle className="w-3 h-3 mr-1" />Activé</Badge>
              : <Badge className="bg-orange-100 text-orange-700 ml-2"><AlertTriangle className="w-3 h-3 mr-1" />Non configuré</Badge>}
          </CardTitle>
          <CardDescription>
            Ce code vous sera demandé avant chaque suppression définitive dans la plateforme.
            Seul vous le connaissez.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!config?.pin_configured ? (
            <>
              <div className="bg-orange-100 border border-orange-300 rounded p-3 text-sm text-orange-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                <strong>Recommandé :</strong> Configurez un PIN pour empêcher toute suppression accidentelle ou non autorisée.
              </div>
              {!showPinSetup ? (
                <Button onClick={() => setShowPinSetup(true)} className="bg-blue-600">
                  <Lock className="w-4 h-4 mr-2" /> Configurer mon PIN
                </Button>
              ) : (
                <div className="space-y-3 bg-white p-4 border rounded-lg">
                  <div>
                    <Label>Votre PIN (min 4 chiffres)</Label>
                    <div className="relative mt-1">
                      <Input
                        type={showPins ? "text" : "password"}
                        value={pinForm.pin}
                        onChange={e => setPinForm(p => ({ ...p, pin: e.target.value }))}
                        placeholder="ex: 7291"
                        maxLength={10}
                        className="text-center text-2xl tracking-widest"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Confirmer le PIN</Label>
                    <Input
                      type={showPins ? "text" : "password"}
                      value={pinForm.confirmation}
                      onChange={e => setPinForm(p => ({ ...p, confirmation: e.target.value }))}
                      placeholder="Répétez le PIN"
                      maxLength={10}
                      className="mt-1 text-center text-2xl tracking-widest"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowPins(p => !p)} className="text-slate-500">
                      {showPins ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {showPins ? " Masquer" : " Voir"}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowPinSetup(false)}>Annuler</Button>
                    <Button onClick={setupPin} disabled={savingPin} className="bg-blue-600">
                      {savingPin ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                      Activer le PIN
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Toutes les suppressions définitives dans la plateforme demandent votre PIN.
              </div>
              {!showPinChange ? (
                <Button variant="outline" onClick={() => setShowPinChange(true)}>
                  <Key className="w-4 h-4 mr-2" /> Changer le PIN
                </Button>
              ) : (
                <div className="space-y-3 bg-white p-4 border rounded-lg">
                  <div>
                    <Label>Ancien PIN</Label>
                    <Input type={showPins ? "text" : "password"} value={changeForm.ancien}
                      onChange={e => setChangeForm(p => ({ ...p, ancien: e.target.value }))}
                      className="mt-1 text-center text-xl tracking-widest" maxLength={10} />
                  </div>
                  <div>
                    <Label>Nouveau PIN</Label>
                    <Input type={showPins ? "text" : "password"} value={changeForm.nouveau}
                      onChange={e => setChangeForm(p => ({ ...p, nouveau: e.target.value }))}
                      className="mt-1 text-center text-xl tracking-widest" maxLength={10} />
                  </div>
                  <div>
                    <Label>Confirmer le nouveau PIN</Label>
                    <Input type={showPins ? "text" : "password"} value={changeForm.confirmation}
                      onChange={e => setChangeForm(p => ({ ...p, confirmation: e.target.value }))}
                      className="mt-1 text-center text-xl tracking-widest" maxLength={10} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setShowPins(p => !p)} className="text-slate-500">
                      {showPins ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {showPins ? " Masquer" : " Voir"}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowPinChange(false)}>Annuler</Button>
                    <Button onClick={changePin} disabled={savingPin} className="bg-blue-600">
                      {savingPin ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                      Changer le PIN
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* SECTION ONBOARDING */}
      <Card className={config?.onboarding_actif !== false ? 'border-green-200' : 'border-amber-200 bg-amber-50'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Restrictions première connexion
            {config?.onboarding_actif !== false
              ? <Badge className="bg-green-100 text-green-700 ml-2"><CheckCircle className="w-3 h-3 mr-1" />Actif</Badge>
              : <Badge className="bg-amber-100 text-amber-700 ml-2"><AlertTriangle className="w-3 h-3 mr-1" />Désactivé</Badge>}
          </CardTitle>
          <CardDescription>
            Quand activé, les employés doivent changer leur mot de passe, accepter la géolocalisation et ajouter leur photo de profil à la première connexion.
            Désactivez temporairement pour tester les comptes employés sans ces restrictions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-700">
                {config?.onboarding_actif !== false ? 'Les restrictions sont actives' : 'Les restrictions sont désactivées — mode test'}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {config?.onboarding_actif !== false 
                  ? 'Les employés verront les écrans de changement de mot de passe, géolocalisation et photo à leur première connexion.'
                  : 'Vous pouvez vous connecter avec n\'importe quel compte sans être bloqué. Pensez à réactiver avant de donner les accès !'}
              </p>
            </div>
            <Button
              data-testid="toggle-onboarding-btn"
              variant={config?.onboarding_actif !== false ? "outline" : "default"}
              className={config?.onboarding_actif !== false ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "bg-green-600 hover:bg-green-700 text-white"}
              onClick={async () => {
                const newState = config?.onboarding_actif === false;
                try {
                  const r = await axios.post(`${API_URL}/api/security/onboarding/toggle`, { actif: newState });
                  if (r.status === 200) {
                    setConfig(prev => ({ ...prev, onboarding_actif: newState }));
                    toast.success(newState ? 'Restrictions réactivées' : 'Restrictions désactivées — mode test');
                  }
                } catch { toast.error('Erreur'); }
              }}
            >
              {config?.onboarding_actif !== false ? 'Désactiver (mode test)' : 'Réactiver les restrictions'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SECTION JOURNAL DE CONNEXIONS */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" />
            Journal de connexions
          </CardTitle>
          <CardDescription>
            Surveillez les connexions à la plateforme. Les comptes sont automatiquement verrouillés après le nombre de tentatives échouées configuré ci-dessus.
            Une alerte par courriel est envoyée lors d'une connexion depuis un nouvel appareil/IP sur le compte propriétaire.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            variant="outline"
            data-testid="btn-voir-logs"
            onClick={async () => {
              try {
                const r = await axios.get(`${API_URL}/api/security/login-logs?limit=50`);
                if (r.status === 200) { setLoginLogs(r.data); setShowLogs(true); }
              } catch { toast.error('Erreur chargement'); }
            }}
          >
            {showLogs ? 'Actualiser' : 'Voir le journal'}
          </Button>

          {showLogs && loginLogs && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                  <p className="text-xs text-green-600 font-semibold uppercase">Connexions réussies</p>
                  <p className="text-xl font-bold text-green-800">{loginLogs.stats.total_connexions}</p>
                </div>
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                  <p className="text-xs text-red-600 font-semibold uppercase">Tentatives échouées</p>
                  <p className="text-xl font-bold text-red-800">{loginLogs.stats.total_echecs}</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${loginLogs.stats.comptes_verrouilles > 0 ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border border-slate-200'}`}>
                  <p className="text-xs text-slate-600 font-semibold uppercase">Comptes verrouillés</p>
                  <p className={`text-xl font-bold ${loginLogs.stats.comptes_verrouilles > 0 ? 'text-red-800' : 'text-slate-800'}`}>{loginLogs.stats.comptes_verrouilles}</p>
                </div>
              </div>

              {/* Comptes verrouillés */}
              {loginLogs.comptes_verrouilles?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-bold text-red-800 mb-2">Comptes verrouillés :</p>
                  {loginLogs.comptes_verrouilles.map(c => (
                    <div key={c.id} className="flex items-center justify-between p-2 bg-white rounded border mb-1">
                      <span className="text-sm">{c.prenom} {c.nom} ({c.email})</span>
                      <Button size="sm" variant="outline" className="border-green-300 text-green-700 hover:bg-green-50"
                        onClick={async () => {
                          try {
                            const r = await axios.post(`${API_URL}/api/security/unlock-account`, { user_id: c.id });
                            if (r.status === 200) { toast.success('Compte déverrouillé'); setShowLogs(false); }
                          } catch { toast.error('Erreur'); }
                        }}
                      >
                        Débloquer
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Dernières connexions */}
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Date</th>
                      <th className="p-2 text-left">Utilisateur</th>
                      <th className="p-2 text-left">IP</th>
                      <th className="p-2 text-center">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loginLogs.logs.map((log, i) => (
                      <tr key={log.id || log._id || i} className={`border-b ${log.success ? '' : 'bg-red-50'}`}>
                        <td className="p-2 whitespace-nowrap">{new Date(log.date).toLocaleString('fr-CA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="p-2">{log.prenom ? `${log.prenom} ${log.nom}` : log.email}</td>
                        <td className="p-2 font-mono text-slate-500">{log.ip?.split(',')[0]}</td>
                        <td className="p-2 text-center">
                          {log.success 
                            ? <Badge className="bg-green-100 text-green-700 text-[10px]">OK</Badge>
                            : <Badge className="bg-red-100 text-red-700 text-[10px]">{log.raison === 'utilisateur_inconnu' ? 'Inconnu' : 'Mauvais mdp'}</Badge>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SECTION BACKUP */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Sauvegardes automatiques
          </CardTitle>
          <CardDescription>
            Votre base de données complète est sauvegardée automatiquement chaque nuit à 3h du matin.
            Vous pouvez aussi lancer un backup manuel à tout moment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-slate-50 p-3 rounded-lg text-center">
              <p className="text-xs text-slate-500">Backups disponibles</p>
              <p className="text-2xl font-bold text-blue-600">{backups.length}</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg text-center">
              <p className="text-xs text-slate-500">Dernier backup</p>
              <p className="text-sm font-medium text-slate-700">
                {backups[0]
                  ? new Date(backups[0].timestamp.replace(/(\d{8})_(\d{6})/, (_, d, t) =>
                      `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}T${t.slice(0,2)}:${t.slice(2,4)}:${t.slice(4,6)}`
                    )).toLocaleString('fr-CA')
                  : 'Jamais'}
              </p>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg text-center">
              <p className="text-xs text-slate-500">Documents sauvegardés</p>
              <p className="text-2xl font-bold text-green-600">{backups[0]?.total_docs?.toLocaleString() || '0'}</p>
            </div>
          </div>

          <Button onClick={runBackup} disabled={backingUp} className="bg-green-600 hover:bg-green-700">
            {backingUp ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {backingUp ? "Sauvegarde en cours..." : "Lancer une sauvegarde maintenant"}
          </Button>

          {backups.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-600">Historique des sauvegardes</p>
              <div className="max-h-64 overflow-y-auto space-y-1">
                {backups.map((bk, idx) => (
                  <div key={bk.timestamp} className="flex items-center justify-between p-3 border rounded-lg bg-slate-50 text-sm">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div>
                        <p className="font-medium">
                          {new Date(bk.timestamp.replace(/(\d{8})_(\d{6})/, (_, d, t) =>
                            `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}T${t.slice(0,2)}:${t.slice(2,4)}:${t.slice(4,6)}`
                          )).toLocaleString('fr-CA')}
                        </p>
                        <p className="text-xs text-slate-500">{bk.total_docs?.toLocaleString()} docs — {bk.size_kb} KB</p>
                      </div>
                    </div>
                    {bk.available && (
                      <a href={`${API_URL}/api/security/backup/download/${bk.timestamp}`}
                        target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline">
                          <Download className="w-3 h-3 mr-1" /> Télécharger
                        </Button>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* SECTION EXPLICATION PUNCH */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <Clock className="w-5 h-5" />
            Protection des pointages actifs
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-blue-800 space-y-2">
          <p>
            <strong>✅ Vos chronomètres ne s'arrêtent jamais</strong> lors des mises à jour de la plateforme.
          </p>
          <p>
            Voici pourquoi : quand un employé punch IN, l'heure de début est <strong>sauvegardée immédiatement
            dans la base de données</strong>. Le chronomètre que vous voyez à l'écran est juste
            un calcul : <em>(heure actuelle) - (heure enregistrée en DB)</em>.
          </p>
          <p>
            Même si le serveur redémarre pendant 3 secondes pour appliquer une mise à jour :
          </p>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>L'heure de punch IN est déjà en DB — elle ne se perd jamais</li>
            <li>Le chronomètre se recalcule automatiquement au rechargement</li>
            <li>Aucune heure travaillée n'est perdue</li>
          </ul>
          <p className="text-xs text-blue-600 bg-blue-100 p-2 rounded">
            💡 Le seul moment risqué : si le serveur tombe en panne pendant exactement le punch OUT
            (fraction de seconde). Pour éliminer ce risque, les mises à jour importantes sont
            déployées en dehors des heures de travail.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
